import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircleRounded,
  UploadFileRounded,
  ErrorOutlineRounded,
  SchoolRounded,
  DescriptionRounded,
} from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import { storage } from '../../lib/firebase';
import { errorText } from '../../lib/error-text';
import { callable } from '../../lib/callable';

// ─── Types ──────────────────────────────────────────────────────────────────

type FieldType = 'text' | 'textarea' | 'email' | 'phone' | 'date' | 'select' | 'checkbox' | 'file';

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  helpText?: string;
  accept?: string;
  maxSizeMb?: number;
}

interface FormTemplate {
  id: string;
  name: string;
  welcomeMessage?: string;
  confirmationMessage?: string;
  fields: FormField[];
}

interface AdmissionPortalData {
  admissionId: string;
  /** Name returned as 'applicantName' from the use case */
  applicantName: string;
  programName: string;
  termName: string;
  status: string;
  formTemplate: FormTemplate | null;
  alreadyCompleted: boolean;
  existingFormData?: Record<string, string | boolean>;
}

interface UploadState {
  progress: number;
  done: boolean;
  storagePath: string;
  fileName: string;
  error?: string;
}

// ─── File Upload Field ─────────────────────────────────────────────────────────

interface FileFieldProps {
  field: FormField;
  token: string;
  uploadState: UploadState | undefined;
  onUploadComplete: (fieldId: string, storagePath: string, fileName: string) => void;
  onUploadError: (fieldId: string, msg: string) => void;
}

function FileFieldInput({
  field,
  token,
  uploadState,
  onUploadComplete,
  onUploadError,
}: FileFieldProps) {
  const { t } = useTranslation('sis');
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = (file: File) => {
    setUploading(true);
    setProgress(0);

    const path = `applicant/${token}/${field.id}_${file.name}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file, { contentType: file.type });

    task.on(
      'state_changed',
      (snap) => setProgress((snap.bytesTransferred / snap.totalBytes) * 100),
      (err) => {
        setUploading(false);
        onUploadError(field.id, err.message);
      },
      // S0·SEC-6 (4): sin getDownloadURL. El postulante no necesita una URL de
      // lo que acaba de subir, y la carpeta deja de poder leerse desde el cliente.
      () => {
        setUploading(false);
        onUploadComplete(field.id, path, file.name);
      },
    );
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
      {uploadState?.done ? (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid rgba(16,185,129,0.3)',
            background: 'rgba(16,185,129,0.05)',
          }}
        >
          <CheckCircleRounded sx={{ color: 'success.main', fontSize: 18 }} />
          <Typography variant="body2" color="success.main" fontWeight={600} flex={1}>
            {uploadState.fileName}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => inputRef.current?.click()}
            sx={{ fontSize: 12 }}
          >
            {t('admissions.applicantUpload.change')}
          </Button>
        </Paper>
      ) : (
        <Box>
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileRounded />}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            sx={{ borderRadius: 2, mb: uploading ? 1 : 0 }}
          >
            {uploading
              ? t('admissions.applicantUpload.uploading', { percent: Math.round(progress) })
              : t('admissions.applicantUpload.choose')}
          </Button>
          {uploading && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ borderRadius: 1, height: 4 }}
            />
          )}
          {uploadState?.error && (
            <Typography variant="caption" color="error" display="block" mt={0.5}>
              {uploadState.error}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
            {t('admissions.applicantUpload.hint')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Dynamic Form Field ─────────────────────────────────────────────────────────

interface DynamicFieldProps {
  field: FormField;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  token: string;
  uploadStates: Record<string, UploadState>;
  onUploadComplete: (fieldId: string, storagePath: string, fileName: string) => void;
  onUploadError: (fieldId: string, msg: string) => void;
}

function DynamicField({
  field,
  value,
  onChange,
  token,
  uploadStates,
  onUploadComplete,
  onUploadError,
}: DynamicFieldProps) {
  const sharedSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  switch (field.type) {
    case 'textarea':
      return (
        <TextField
          label={field.label}
          required={field.required}
          helperText={field.helpText}
          multiline
          minRows={3}
          fullWidth
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          sx={sharedSx}
        />
      );
    case 'select':
      return (
        <FormControl fullWidth required={field.required}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            label={field.label}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            sx={{ borderRadius: 2 }}
          >
            {(field.options ?? []).map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {field.helpText && (
            <Typography variant="caption" color="text.secondary" mt={0.5} ml={1.5}>
              {field.helpText}
            </Typography>
          )}
        </FormControl>
      );
    case 'checkbox':
      return (
        <FormControlLabel
          required={field.required}
          control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2">{field.label}</Typography>
              {field.helpText && (
                <Typography variant="caption" color="text.secondary">
                  {field.helpText}
                </Typography>
              )}
            </Box>
          }
        />
      );
    case 'file':
      return (
        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>
            {field.label}
            {field.required && ' *'}
          </Typography>
          {field.helpText && (
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {field.helpText}
            </Typography>
          )}
          <FileFieldInput
            field={field}
            token={token}
            uploadState={uploadStates[field.id]}
            onUploadComplete={onUploadComplete}
            onUploadError={onUploadError}
          />
        </Box>
      );
    default:
      return (
        <TextField
          label={field.label}
          required={field.required}
          helperText={field.helpText}
          type={
            field.type === 'email'
              ? 'email'
              : field.type === 'date'
                ? 'date'
                : field.type === 'phone'
                  ? 'tel'
                  : 'text'
          }
          fullWidth
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          {...(field.type === 'date' ? { InputLabelProps: { shrink: true } } : {})}
          sx={sharedSx}
        />
      );
  }
}

// ─── Main Public Portal Page ─────────────────────────────────────────────────

export function ApplyPage() {
  // tenantId is present in new-style URLs (/apply/:tenantId/:token)
  // and absent in old-style URLs (/apply/:token) — the CF handles both cases
  const { tenantId, token } = useParams<{ tenantId?: string; token: string }>();

  const [portalData, setPortalData] = useState<AdmissionPortalData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [formValues, setFormValues] = useState<Record<string, string | boolean>>({});
  const [uploadStates, setUploadStates] = useState<Record<string, UploadState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Load admission data
  useEffect(() => {
    if (!token) return;
    const fn = callable('getAdmissionByToken');
    fn({ token, ...(tenantId ? { tenantId } : {}) })
      .then((result) => {
        const data = result.data as AdmissionPortalData;
        setPortalData(data);
        // Pre-fill empty strings for all text fields from template
        const initial: Record<string, string | boolean> = {};
        (data.formTemplate?.fields ?? []).forEach((f) => {
          initial[f.id] = f.type === 'checkbox' ? false : '';
        });
        // If re-visiting a completed form, pre-populate existing values
        if (data.existingFormData) {
          Object.assign(initial, data.existingFormData);
        }
        setFormValues(initial);
      })
      .catch((err) => {
        // La clave viaja desde el servidor (link.invalid_or_expired): se traduce.
        setLoadError(errorText(err));
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Handle Upload Complete
  const handleUploadComplete = (fieldId: string, storagePath: string, fileName: string) => {
    setUploadStates((prev) => ({
      ...prev,
      [fieldId]: { progress: 100, done: true, storagePath, fileName },
    }));
  };

  const handleUploadError = (fieldId: string, msg: string) => {
    setUploadStates((prev) => ({
      ...prev,
      [fieldId]: { progress: 0, done: false, storagePath: '', fileName: '', error: msg },
    }));
  };

  const handleSubmit = async () => {
    if (!token || !portalData) return;

    // Validate required fields
    const fields = portalData.formTemplate?.fields ?? [];
    const missing: string[] = [];
    fields.forEach((f) => {
      if (!f.required) return;
      if (f.type === 'file') {
        if (!uploadStates[f.id]?.done) missing.push(f.label);
      } else if (f.type === 'checkbox') {
        if (!formValues[f.id]) missing.push(f.label);
      } else {
        if (!String(formValues[f.id] ?? '').trim()) missing.push(f.label);
      }
    });

    if (missing.length > 0) {
      setSubmitError(`Por favor completa los campos requeridos: ${missing.join(', ')}`);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const documents = Object.entries(uploadStates)
        .filter(([, u]) => u.done)
        .map(([fieldId, u]) => ({
          fieldId,
          type: fieldId,
          storagePath: u.storagePath,
          fileName: u.fileName,
        }));

      const fn = callable('completeAdmissionForm');
      await fn({
        token,
        formData: formValues,
        uploadedDocuments: documents,
        ...(tenantId ? { tenantId } : {}),
      });
      setSubmitted(true);
    } catch (err) {
      const msg = errorText(err, 'Error al enviar el formulario.');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Already completed ──
  if (!loading && portalData?.alreadyCompleted) {
    return (
      <PublicPageWrapper>
        <Stack alignItems="center" gap={2} textAlign="center">
          <CheckCircleRounded sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h5" fontWeight={800} color="white">
            Formulario Ya Enviado
          </Typography>
          <Typography color="text.secondary" maxWidth={420}>
            Ya completaste tu formulario de admisión. El equipo de admisiones revisará tu solicitud
            y te contactará pronto.
          </Typography>
        </Stack>
      </PublicPageWrapper>
    );
  }

  // ── Submitted success ──
  if (submitted) {
    return (
      <PublicPageWrapper>
        <Stack alignItems="center" gap={2} textAlign="center">
          <CheckCircleRounded sx={{ fontSize: 64, color: 'success.main' }} />
          <Typography variant="h5" fontWeight={800} color="white">
            ¡Formulario Enviado Exitosamente!
          </Typography>
          <Typography color="text.secondary" maxWidth={420}>
            Hemos recibido tu información. El equipo de admisiones revisará tu solicitud y te
            contactará en los próximos días.
          </Typography>
          {portalData && (
            <Chip
              label={`${portalData.programName} · ${portalData.termName}`}
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Stack>
      </PublicPageWrapper>
    );
  }

  // ── Error loading ──
  if (!loading && loadError) {
    return (
      <PublicPageWrapper>
        <Stack alignItems="center" gap={2} textAlign="center">
          <ErrorOutlineRounded sx={{ fontSize: 64, color: 'error.main' }} />
          <Typography variant="h5" fontWeight={800} color="white">
            Enlace Inválido
          </Typography>
          <Typography color="text.secondary" maxWidth={420}>
            {loadError} Por favor contacta al equipo de admisiones para obtener un nuevo enlace.
          </Typography>
        </Stack>
      </PublicPageWrapper>
    );
  }

  // ── Loading ──
  if (loading || !portalData) {
    return (
      <PublicPageWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress sx={{ color: '#6366f1' }} />
        </Box>
      </PublicPageWrapper>
    );
  }

  // ── Form ──
  return (
    <PublicPageWrapper>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            mx: 'auto',
            mb: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
          }}
        >
          <SchoolRounded sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Typography variant="h4" fontWeight={800} color="white" gutterBottom>
          Formulario de Admisión
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {portalData.programName}
        </Typography>
        <Stack direction="row" gap={1} justifyContent="center" mt={1.5} flexWrap="wrap">
          <Chip label={portalData.termName} size="small" variant="outlined" />
          <Chip
            icon={<DescriptionRounded />}
            label={`${portalData.formTemplate?.fields.length ?? 0} campos`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          mb={3}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Postulante: <strong style={{ color: 'white' }}>{portalData.applicantName}</strong>
        </Typography>

        {portalData.formTemplate?.welcomeMessage && (
          <Alert
            severity="info"
            icon={<DescriptionRounded />}
            sx={{
              mb: 3,
              borderRadius: 2,
              background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.2)',
            }}
          >
            {portalData.formTemplate.welcomeMessage}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {submitError}
          </Alert>
        )}

        <Stack gap={3}>
          {(portalData.formTemplate?.fields ?? []).map((field) => (
            <Box key={field.id}>
              <DynamicField
                field={field}
                value={formValues[field.id] ?? (field.type === 'checkbox' ? false : '')}
                onChange={(val) => setFormValues((prev) => ({ ...prev, [field.id]: val }))}
                token={token!}
                uploadStates={uploadStates}
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
              />
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              px: 5,
              py: 1.5,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Enviar Formulario'}
          </Button>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.disabled" textAlign="center" display="block" mt={3}>
        Tus datos son seguros y solo serán usados en el proceso de admisión.
      </Typography>
    </PublicPageWrapper>
  );
}

// ─── Public Page Wrapper ─────────────────────────────────────────────────────

function PublicPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1117 0%, #0f1629 50%, #0d1117 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 680 }}>{children}</Box>
    </Box>
  );
}
